import { clsx } from 'clsx';
import React, { ReactElement, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { combineSx, translateSizeToCssValue } from '../../utils';
import { Box } from '../Box';
import { gridClasses } from '../DataTable';
import { LoadingBar } from '../LoadingBar';
import { ContainerActions, ContainerActionsProps } from './ContainerActions';
import { ContainerBody, ContainerBodyProps } from './ContainerBody';
import {
  ContainerContextProvider,
  ContainerModalContextProvider,
  ContainerModalContextProviderProps,
  useContainerContext,
  useHasContainerContext,
  useIsContainerInModal,
} from './ContainerContextProvider';
import { ContainerHeader } from './ContainerHeader';
import { ContainerModal, ContainerModalWithPortal } from './ContainerModal';
import { ContainerHeaderProps, ContainerProps } from './model';
import { useContainerLayout } from './useContainerLayout';

interface ContainerSubComponents {
  Actions: React.ComponentType<ContainerActionsProps>;
  Body: React.ComponentType<ContainerBodyProps>;
  Header: React.ComponentType<ContainerHeaderProps>;
}

type ContainerContentProps = Pick<ContainerProps, 'sx' | 'children' | 'width' | 'height' | 'data-testid' | 'id'>;

const ContainerContent = React.forwardRef<HTMLDivElement, ContainerContentProps>(function ContainerContent(
  { sx, children, width, height, 'data-testid': dataTestId, id },
  ref
) {
  const {
    setIsHovered,
    isShowingFloatingActions,
    collapsed,
    isLoading,
    isFetching,
    isNestedContainer,
    disableBorder,
    disablePadding,
  } = useContainerContext();
  const { isParentContainerInModal } = useIsContainerInModal();
  const adjustedWidth = translateSizeToCssValue(width);
  const showLinearLoadingBar = isLoading || isFetching;

  const containerLayout = useContainerLayout({ disablePadding });

  // Override dimensions to fill the modal when inside one. Otherwise, use the specified height/width.
  const wrapperWidth = isParentContainerInModal ? '100%' : width;
  const wrapperHeight = isParentContainerInModal ? '100%' : height;

  return (
    <Box
      id={id}
      width={wrapperWidth}
      height={collapsed ? 'auto' : wrapperHeight}
      sx={combineSx(
        {
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          padding: '0 !important',
        },
        sx,
        // prevents background color from displaying beyond the container's border.
        { backgroundColor: 'transparent' }
      )}
    >
      {showLinearLoadingBar && (
        <LoadingBar
          sx={(theme) => {
            const borderRadius = isNestedContainer ? theme.shape.nestedContainerRadius : theme.shape.containerRadius;
            return {
              width: `calc(${adjustedWidth} - ${Number(borderRadius.replace('px', '')) * 2}px)`,
              left: borderRadius,
              position: 'absolute',
              zIndex: 1, // Ensures the loading bar appears above the container border
            };
          }}
        />
      )}

      <Box
        ref={ref}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={(event) => {
          // Set isHovered to false if the focus was not shifted to a child element
          // This ensures that the actions remain tabbable
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsHovered(false);
          }
        }}
        // Ensures keyboard accessibility of hover-based actions.
        tabIndex={0}
        className={clsx('Container', collapsed && 'collapsed', isParentContainerInModal && 'isModalParent')}
        data-testid={dataTestId}
        width={width}
        // Override the height when collapsed to ensure the container shrinks
        // when its children do.
        height={collapsed ? 'auto' : height}
        sx={combineSx((theme) => {
          const borderRadius = isNestedContainer ? theme.shape.nestedContainerRadius : theme.shape.containerRadius;
          return {
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            backgroundColor: theme.palette.background.container,
            borderRadius: borderRadius,
            borderTopRightRadius: isShowingFloatingActions ? 0 : borderRadius,
            paddingY: `${containerLayout.container.paddingY}px`,
            paddingX: `${containerLayout.container.paddingX}px`,

            // Page-level panels on the canvas (not nested inside another Container or within a modal)
            ...(theme.featureFlags.panwRebrandStubYear &&
              !isNestedContainer &&
              !isParentContainerInModal &&
              !disableBorder && {
                border: `1px solid ${theme.palette.border.subtle}`,
              }),

            // Overflow/scrolling should be happening in Container.Body
            // However, since not all Container will include a Container.Body, we'll apply this css only when Container.Body is present
            ['has(.container-header):has(.container-body):']: {
              overflow: 'hidden',
            },

            // Hide container padding when DataGrid is the only child
            [`> .${gridClasses.root}:only-child`]: {
              margin: theme.spacing(-1, -2),
              height: 'calc(100% + 16px)',
            },
            // Hide container padding when there is no ContainerHeader and DataGrid is the only child of ContainerBody
            [`:not(:has(> .container-header)):has(.container-body-inner > .${gridClasses.root}:only-child)`]: {
              padding: 0,
            },
            // Hide ContainerBody padding when ContainerBody is the only child of Container
            ['> .container-body:only-child']: {
              ['.container-body-inner']: {
                paddingY: 0,
              },
            },

            // Add borders to nested Containers and the top-level container in a
            // modal.
            // NOTE: the width of these borders should be kept in sync with the `useVizPanelContentLayout`
            // hook in `viz-framework` to ensure consistency.
            ['.Container, &.isModalParent']: {
              border: `${containerLayout.container.nestedBorderWidth}px solid ${theme.palette.border.default}`,
              ['.floating-actions']: {
                border: `${containerLayout.container.nestedBorderWidth}px solid ${theme.palette.border.default}`,
                borderBottom: 'none',
                right: `-${containerLayout.container.nestedBorderWidth}px`, // shift to the right to account for border
              },
            },

            // Adjust the layout when the container is the top-level container in
            // a modal, so it fills the space.
            ['&.isModalParent']: {
              width: '100%',
              height: '100%',
            },

            [`.container-body-inner`]: {
              paddingY: `${containerLayout.containerBodyInner.paddingY}px`,
              paddingX: `${containerLayout.containerBodyInner.paddingX}px`,

              // Ensures that the border radii are retained when padding is disabled.
              borderBottomLeftRadius: `${containerLayout.containerBodyInner.borderBottomLeftRadius}px`,
              borderBottomRightRadius: `${containerLayout.containerBodyInner.borderBottomRightRadius}px`,
            },
          };
        }, sx)}
      >
        {children}
      </Box>
    </Box>
  );
});

// Internal component is split out, so it's possible to also leverage the context.
const InternalContainer = React.forwardRef<HTMLDivElement, ContainerProps>(function InternalContainer(
  { width, height, ContainerModalProps, disablePortal, sx, children, 'data-testid': dataTestId, id },
  ref
) {
  const { isModalEnabled } = useContainerContext();

  const ContainerComponent = isModalEnabled && !disablePortal ? ContainerWithPortal : ContainerWithoutPortal;

  return (
    <ContainerComponent height={height} width={width} ContainerModalProps={ContainerModalProps}>
      <ContainerContent ref={ref} width={width} height={height} sx={sx} data-testid={dataTestId} id={id}>
        {children}
      </ContainerContent>
    </ContainerComponent>
  );
});

interface ContainerRenderingProps extends Pick<ContainerProps, 'height' | 'width' | 'ContainerModalProps'> {
  children: ReactElement<ContainerProps>;
}

// These values are always the same in the "cloned" case for the original (not in modal) and cloned
// (in modal) versions of the container. Defining these outside the render cycle to avoid perf churn
// from the context value changing.
const CONTAINER_MODAL_CONTEXT_VALUE_NOT_IN_MODAL = { isInModal: false };
const CONTAINER_MODAL_CONTEXT_VALUE_IN_MODAL = { isInModal: true };

/**
 * Simple approach to rendering the container when the modal is disabled or the modal is using the
 * default `clone` strategy.
 */
const ContainerWithoutPortal = ({ ContainerModalProps, children }: ContainerRenderingProps) => {
  const { modalOpen, isModalEnabled } = useContainerContext();

  // Note that the container content is rendered inline as is and then cloned in the portal.
  // This should be compatible with container usage prior to the introduction of the modal.
  return (
    <>
      <ContainerModalContextProvider value={CONTAINER_MODAL_CONTEXT_VALUE_NOT_IN_MODAL}>
        {children}
      </ContainerModalContextProvider>
      {isModalEnabled && modalOpen && (
        <ContainerModal {...ContainerModalProps}>
          <ContainerModalContextProvider value={CONTAINER_MODAL_CONTEXT_VALUE_IN_MODAL}>
            {React.cloneElement(children, { width: '100%', height: '100%' })}
          </ContainerModalContextProvider>
        </ContainerModal>
      )}
    </>
  );
};

/**
 * More complex approach to rendering the container when the modal is enabled with the `portal`
 * strategy.
 */
const ContainerWithPortal = ({ height, width, ContainerModalProps, children }: ContainerRenderingProps) => {
  const { modalOpen } = useContainerContext();
  const [inlineContainerParentEl, setInlineContainerParentEl] = useState<HTMLDivElement | null>(null);
  const [portalEl, setPortalEl] = useState<HTMLDivElement | null>(null);

  // The portal-based strategy relies on placing the content inside a portal and then moving
  // the dom node that contains that portal around. See these long react issue discussions for
  // the inspiration for this work. Facebook does some similar things internally, so this is
  // hopefully relatively safe, but it does add some additional complexity.
  // https://github.com/facebook/react/issues/12247
  // https://github.com/facebook/react/issues/13044

  // When using portals, `isInModal` is a stable way to determine whether the container is in the
  // modal or not. However, we use this context, so that consumers can use the same `useIsContainerInModal`
  // hook regardless of which modal strategy they are using.
  const modalContextProviderValue: ContainerModalContextProviderProps['value'] = useMemo(() => {
    return { isInModal: !!modalOpen };
  }, [modalOpen]);

  // Note that the container content is now wrapped in some additional divs in order to enable
  // the portal behavior. This is likely to break preexisting container usage that set
  // layout-related styles directly on `Container` using `sx` and assumed the content would be
  // rendered inline without any additional parents. Teams can work around this by moving those
  // styles to a parent `Box`.
  return (
    <>
      <Box width={width} height={height} ref={setInlineContainerParentEl}>
        <Box ref={setPortalEl} sx={{ width: '100%', height: '100%' }}></Box>
      </Box>
      <ContainerModalContextProvider value={modalContextProviderValue}>
        {portalEl && ReactDOM.createPortal(children, portalEl)}
      </ContainerModalContextProvider>
      {portalEl && inlineContainerParentEl && modalOpen && (
        <ContainerModalWithPortal
          {...ContainerModalProps}
          portalEl={portalEl}
          inlineContainerParentEl={inlineContainerParentEl}
        />
      )}
    </>
  );
};

const Container = React.forwardRef<HTMLDivElement, ContainerProps>(function Container(
  {
    collapsed,
    collapseButtonAriaLabel,
    defaultCollapsed,
    onCollapseButtonClick,
    actions,
    subtleActionsVisibility,
    actionsSize = 'medium',
    menuItems,
    modalOpen,
    defaultModalOpen,
    onOpenModalButtonClick,
    onCloseModal,
    isLoading,
    isFetching,
    draggable,
    disableBorder,
    disablePadding,
    ...otherProps
  },
  ref
) {
  // If the context is set before adding the provider below, this Container has an ancestor that
  // is a Container.
  const isNestedContainer = useHasContainerContext();

  return (
    <ContainerContextProvider
      value={{
        collapsed,
        collapseButtonAriaLabel,
        defaultCollapsed,
        onCollapseButtonClick,
        actions,
        subtleActionsVisibility,
        actionsSize,
        menuItems,
        draggable,
        modalOpen,
        defaultModalOpen,
        onOpenModalButtonClick,
        onCloseModal,
        isLoading,
        isFetching,
        isNestedContainer,
        disableBorder,
        disablePadding,
      }}
    >
      <InternalContainer {...otherProps} ref={ref} />
    </ContainerContextProvider>
  );
});

// Workaround to get the nested components working well with forwardRef and
// types without needing to cast, which is yucky.
// Related SO article:
// https://stackoverflow.com/questions/63453536/jsx-dot-notation-with-forwardref-typescript
const ContainerNamespace: typeof Container & ContainerSubComponents = Object.assign(Container, {
  Actions: ContainerActions,
  Header: ContainerHeader,
  Body: ContainerBody,
});
export { ContainerNamespace as Container };
